import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type {
  ExternalApiConfig,
  ExternalApiCredentials,
  ExternalApiScopes,
  EnableExternalApiPayload,
  UpdateExternalApiPayload,
} from "@/types";

export const useExternalApi = (portalId: string) => {
  const queryClient = useQueryClient();

  // Query: get external API config for this portal
  const configQuery = useQuery({
    queryKey: QUERY_KEYS.EXTERNAL_API.CONFIG(portalId),
    queryFn: async () => {
      const response = await apiClient.get<ExternalApiConfig>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.CONFIG(portalId)
      );
      return response.data;
    },
    enabled: !!portalId,
  });

  const apiEnabled = configQuery.data?.enabled ?? false;

  // Query: available + granted scopes (only when the API is enabled)
  const scopesQuery = useQuery({
    queryKey: ["external-api-scopes", portalId],
    queryFn: async () => {
      const response = await apiClient.get<ExternalApiScopes>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.SCOPES(portalId)
      );
      return response.data;
    },
    enabled: !!portalId && apiEnabled,
  });

  // Mutation: replace the granted scopes
  const updateScopesMutation = useMutation({
    mutationKey: ["update-external-api-scopes"],
    mutationFn: async (scopes: string[]) => {
      const response = await apiClient.put<ExternalApiScopes>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.SCOPES(portalId),
        { scopes }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external-api-scopes", portalId] });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXTERNAL_API.ALL(portalId),
      });
    },
  });

  // Mutation: enable external API (creates Cognito app client)
  const enableMutation = useMutation({
    mutationKey: [MUTATION_KEYS.ENABLE_EXTERNAL_API],
    mutationFn: async (payload: EnableExternalApiPayload | void) => {
      const response = await apiClient.post<ExternalApiCredentials>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.CONFIG(portalId),
        payload || {}
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXTERNAL_API.ALL(portalId),
      });
    },
  });

  // Mutation: disable external API (deletes Cognito app client)
  const disableMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DISABLE_EXTERNAL_API],
    mutationFn: async () => {
      await apiClient.delete(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.CONFIG(portalId)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXTERNAL_API.ALL(portalId),
      });
    },
  });

  // Mutation: rotate client secret
  const rotateMutation = useMutation({
    mutationKey: [MUTATION_KEYS.ROTATE_EXTERNAL_API_SECRET],
    mutationFn: async () => {
      const response = await apiClient.post<ExternalApiCredentials>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.ROTATE(portalId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXTERNAL_API.ALL(portalId),
      });
    },
  });

  // Mutation: update external API settings (e.g., default agent)
  const updateMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_EXTERNAL_API],
    mutationFn: async (payload: UpdateExternalApiPayload) => {
      const response = await apiClient.patch<ExternalApiConfig>(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.CONFIG(portalId),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXTERNAL_API.ALL(portalId),
      });
    },
  });

  return {
    config: configQuery.data ?? null,
    isLoading: configQuery.isLoading,
    error: configQuery.error,

    enableApi: enableMutation.mutateAsync,
    isEnabling: enableMutation.isPending,

    disableApi: disableMutation.mutate,
    isDisabling: disableMutation.isPending,

    rotateSecret: rotateMutation.mutateAsync,
    isRotating: rotateMutation.isPending,

    updateApi: updateMutation.mutate,
    isUpdating: updateMutation.isPending,

    scopes: scopesQuery.data ?? null,
    isLoadingScopes: scopesQuery.isLoading,
    updateScopes: updateScopesMutation.mutateAsync,
    isUpdatingScopes: updateScopesMutation.isPending,
  };
};
